import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, Search, Network, FileCode, Bug, Gauge, Replace,
  AlertTriangle, ExternalLink, Copy, Fingerprint, HardDrive, BookLock,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import SiteFooter from './SiteFooter.jsx';

/**
 * /nachpruefen  ("Quelltext untersuchen")
 *
 * Das ist bewusst NICHT der Maschinenraum. Der Maschinenraum zeigt live, was
 * gerade mit einer konkreten Datei passiert, und laeuft nur angemeldet. Diese
 * Seite beantwortet die andere Frage, und zwar ohne Konto: Laeuft in deinem
 * Browser wirklich der Code, den wir beschreiben?
 * Beides getrennt zu halten war eine ausdrueckliche Vorgabe. Nicht wieder auf
 * den Maschinenraum verlinken: fuer abgemeldete Leser, also fuer die Mehrheit
 * dieser Seite, geht der Link ins Leere.
 *
 * HARTE REGEL FUER DIESE DATEI:
 * Auf dieser Seite steht keine einzige fest eingetippte Zahl ueber den Build.
 * Dateinamen enthalten Content-Hashes, Byte-Groessen und Trefferzahlen aendern
 * sich mit jedem Deploy. Wer so etwas hartkodiert, baut eine Seite, die beim
 * naechsten Build zur Luege wird, und zwar ausgerechnet gegenueber den Leuten,
 * die hier am genauesten hinsehen. Deshalb wird alles, was gezaehlt wird, im
 * Browser des Lesers zur Anzeigezeit gezaehlt.
 *
 * Zweite Regel: Diese Seite darf nicht mehr behaupten, als sie beweist.
 * Client-seitige Pruefung zeigt, was DEIN Browser HEUTE in DIESER Sitzung
 * ausgefuehrt hat. Sie sagt nichts darueber, was der Server morgen ausliefert.
 * Dieser Satz steht weit oben und nicht im Kleingedruckten.
 */

const WORKER_RE = /cryptoWorker-[A-Za-z0-9_-]+\.js/;
const BUNDLE_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js$/;

// Begriffe, die im Krypto-Worker NICHT vorkommen duerfen. Findet hier jemand
// einen Treffer, ist entweder der Worker veraendert worden oder diese Seite
// falsch. Beides will man wissen.
const FORBIDDEN = [
  { term: 'fetch', why: 'HTTP-Anfrage' },
  { term: 'XMLHttpRequest', why: 'HTTP-Anfrage, alte Schreibweise' },
  { term: 'sendBeacon', why: 'stiller Versand im Hintergrund' },
  { term: 'WebSocket', why: 'Dauerverbindung' },
  { term: 'EventSource', why: 'Serverstrom' },
  { term: 'password', why: 'Passwort' },
  { term: 'passwort', why: 'Passwort, deutsch' },
  { term: 'PBKDF2', why: 'Schluesselableitung aus einem Passwort' },
  { term: 'deriveKey', why: 'Schluesselableitung' },
  { term: 'deriveBits', why: 'Schluesselableitung' },
];

// Begriffe, die drin sein MUESSEN. Ein Worker ohne AES-GCM waere kein Worker,
// der verschluesselt.
const EXPECTED = [
  { term: 'AES-GCM', why: 'das tatsaechlich benutzte Verfahren' },
  { term: 'encrypt', why: 'verschluesseln' },
  { term: 'decrypt', why: 'entschluesseln' },
];

// Suchbegriffe fuer Strg+Shift+F in den Entwicklertools.
//
// ACHTUNG, hier steckt eine Selbstbezugs-Falle drin, in die wir schon einmal
// getreten sind: Sobald der Suchbegriff hier woertlich im Quelltext steht,
// landet er beim Bauen im ausgelieferten Bundle. Dann findet der Leser beim
// Suchen nicht nur den echten Code, sondern auch diese Seite, die ihm gerade
// das Suchen erklaert. Aus einem sauberen Treffer werden drei, und die
// Anleitung sieht kaputt aus.
//
// Deshalb stehen die Begriffe hier zerlegt und werden erst zur Laufzeit
// zusammengesetzt. Array.join() faltet kein Minifier zusammen, ein
// 'a' + 'b' dagegen schon. Wer hier etwas aendert, muss die Trefferzahlen
// danach gegenmessen.
const ANCHORS = [
  {
    parts: ['It never receives', ' a password.'],
    where: 'Kommentar in cryptoWorker.js',
    note: 'Steht nur im Quelltext, weil der Minifier Kommentare wegwirft.',
  },
  {
    parts: ['There is deliberately NO', ' password-to-master-key derivation'],
    where: 'Kommentar in crypto.js',
    note: 'Zwei Treffer sind hier richtig, einer je Bundle. Beide binden dieselbe crypto.js ein. Genau das ist der Beweis, dass der Worker keine zweite, andere Krypto benutzt.',
  },
  {
    parts: ['6', 'e5'],
    where: 'die echte Iterationszahl im ausgelieferten Code',
    note: 'Der Minifier kuerzt sechshunderttausend auf diese Kurzschreibweise ein. Suchen Sie nicht nach der ausgeschriebenen Zahl: die finden Sie zwar auch, aber nur als Code-Zitat auf der Doku-Seite und nicht als Code, der laeuft.',
  },
].map((a) => ({ ...a, term: a.parts.join('') }));

function countOccurrences(haystack, needle) {
  if (!needle) return 0;
  let n = 0;
  let i = haystack.indexOf(needle);
  while (i !== -1) { n += 1; i = haystack.indexOf(needle, i + needle.length); }
  return n;
}

/**
 * Kettenglied eines Logbuch-Eintrags, zeichengleich zu scripts/pruefwerte.mjs.
 * Die Reihenfolge der Felder steht hier fest ausgeschrieben und darf sich nicht
 * aendern, sonst rechnet der Browser eine andere Kette als der Build und die
 * Seite schlaegt grundlos Alarm.
 */
async function kettengliedHex(vorher, eintrag) {
  const inhalt = JSON.stringify({
    stand: eintrag.stand,
    bundle: { datei: eintrag.bundle.datei, bytes: eintrag.bundle.bytes, sha256: eintrag.bundle.sha256 },
    worker: { datei: eintrag.worker.datei, bytes: eintrag.worker.bytes, sha256: eintrag.worker.sha256 },
  });
  return sha256Hex(new TextEncoder().encode(vorher + inhalt));
}

/**
 * Archivadresse eines Logbuch-Eintrags. Der Zielteil muss zeichengleich zu
 * eintragsUrl() in scripts/pruefwerte.mjs sein, sonst zeigt der Link ins Leere.
 *
 * Mit `stempel` wird der Schnappschuss festgenagelt, ohne ihn nimmt das Archiv
 * den neuesten zu dieser Adresse. Beides hat einen Haken, deshalb steht beides
 * auf der Seite: der Stempel kommt aus unserem Logbuch, den koennten wir setzen,
 * wohin wir wollen. Die Fassung ohne Stempel kommt allein vom Archiv, hinkt aber
 * dem Index hinterher und leitet direkt nach einer Veroeffentlichung noch auf
 * die Speicherseite weiter. Der Leser bekommt darum den festgenagelten Link zum
 * Klicken und den Weg ohne Stempel danebengeschrieben.
 */
function archivUrl(stand, stempel = null) {
  const ziel = `https://tresor.datenputzer.de/pruefwerte.json?stand=${encodeURIComponent(stand)}`;
  return `https://web.archive.org/web/${stempel || '2'}/${ziel}`;
}

async function sha256Hex(buffer) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function CopyButton({ value, label = 'Kopieren' }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="np-btn np-btn-ghost"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          window.setTimeout(() => setDone(false), 1600);
        } catch { /* Zwischenablage gesperrt, dann eben von Hand markieren */ }
      }}
    >
      <Copy className="np-btn-icon" aria-hidden="true" />
      {done ? 'Kopiert' : label}
    </button>
  );
}

function Verdict({ ok, children }) {
  return (
    <span className={`np-verdict ${ok ? 'is-ok' : 'is-bad'}`}>
      {children}
    </span>
  );
}

// Gleiche Struktur wie die Inhaltsleiste auf /docs: eine Liste, daraus die
// Navigation, daraus der Scrollspy. Vorher war das handverdrahtetes Markup ohne
// aktive Markierung und ohne Aufklapper auf dem Handy.
const NP_SECTIONS = [
  { id: 'was-das-ist', label: 'Was diese Seite beweist' },
  { id: 'deine-sitzung', label: 'Ihre Sitzung' },
  { id: 'beweis-logbuch', label: 'Das Logbuch' },
  { id: 'beweis-worker', label: '1. Der Worker, komplett' },
  { id: 'beweis-netzwerk', label: '2. Wohin Ihr Browser spricht' },
  { id: 'beweis-baum', label: '3. Läuft wirklich diese Datei?' },
  { id: 'beweis-haltepunkt', label: '4. Haltepunkt im Klartext' },
  { id: 'beweis-coverage', label: '5. Was davon lief' },
  { id: 'beweis-override', label: '6. Der Gegenbeweis' },
  { id: 'grenzen', label: 'Grenzen' },
];

const MOBILE_TOC_MQ = '(max-width: 900px)';

export default function NachpruefenPage() {
  const [live, setLive] = useState({ status: 'loading' });
  const [anchorCounts, setAnchorCounts] = useState(null);
  const [anchorBusy, setAnchorBusy] = useState(false);
  const [logbuch, setLogbuch] = useState({ status: 'loading' });
  const [activeId, setActiveId] = useState(NP_SECTIONS[0].id);
  const [tocOpen, setTocOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !window.matchMedia(MOBILE_TOC_MQ).matches;
  });

  // Alles Browser-Abhaengige passiert hier drin. Die Seite wird beim Build
  // vorgerendert, und im Node-Vorrendern gibt es weder document noch fetch.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const scripts = Array.from(document.querySelectorAll('script[src]')).map((s) => s.src);
        const bundleUrl = scripts.find((src) => BUNDLE_RE.test(new URL(src, window.location.href).pathname));
        if (!bundleUrl) throw new Error('Haupt-Bundle nicht gefunden');

        const bundleRes = await fetch(bundleUrl, { cache: 'force-cache' });
        if (!bundleRes.ok) throw new Error(`Bundle nicht ladbar (${bundleRes.status})`);
        const bundleBuf = await bundleRes.arrayBuffer();
        const bundleText = new TextDecoder().decode(bundleBuf);

        // Den Worker-Dateinamen lesen wir aus dem Haupt-Bundle heraus, genau wie
        // der Browser es tut. Damit stimmt die Adresse auch nach jedem Deploy,
        // ohne dass jemand diese Seite nachpflegen muss.
        const match = bundleText.match(WORKER_RE);
        if (!match) throw new Error('Worker-Dateiname steht nicht im Bundle');
        const workerUrl = new URL(`/assets/${match[0]}`, window.location.origin).href;

        const workerRes = await fetch(workerUrl, { cache: 'force-cache' });
        if (!workerRes.ok) throw new Error(`Worker nicht ladbar (${workerRes.status})`);
        const workerBuf = await workerRes.arrayBuffer();
        const workerText = new TextDecoder().decode(workerBuf);

        const [bundleHash, workerHash] = await Promise.all([
          sha256Hex(bundleBuf), sha256Hex(workerBuf),
        ]);

        if (cancelled) return;
        setLive({
          status: 'ready',
          bundleUrl, workerUrl,
          bundleBytes: bundleBuf.byteLength,
          workerBytes: workerBuf.byteLength,
          bundleHash, workerHash,
          workerText,
          hasSourceMap: /sourceMappingURL/.test(workerText),
          forbidden: FORBIDDEN.map((f) => ({ ...f, count: countOccurrences(workerText.toLowerCase(), f.term.toLowerCase()) })),
          expected: EXPECTED.map((f) => ({ ...f, count: countOccurrences(workerText.toLowerCase(), f.term.toLowerCase()) })),
        });
      } catch (err) {
        if (!cancelled) setLive({ status: 'error', message: err?.message || 'unbekannter Fehler' });
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Logbuch getrennt vom Rest laden. Wenn das Logbuch fehlt, sollen die
  // uebrigen Beweise trotzdem funktionieren.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // no-store, nicht force-cache: bei den Bundles wollen wir ausdruecklich
        // die Datei pruefen, die wirklich lief. Beim Logbuch ist das Gegenteil
        // richtig, ein alter Stand aus dem Zwischenspeicher wuerde einen
        // frischen Pruefwert als fehlend anzeigen und damit Fehlalarm ausloesen.
        const res = await fetch('/pruefwerte.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`Logbuch nicht abrufbar (${res.status})`);
        const daten = await res.json();
        if (!Array.isArray(daten.eintraege) || daten.eintraege.length === 0) {
          throw new Error('Logbuch enthaelt keine Eintraege');
        }
        let glied = '';
        let ketteHeil = true;
        let bruchBei = null;
        for (let i = 0; i < daten.eintraege.length; i += 1) {
          const e = daten.eintraege[i];
          // eslint-disable-next-line no-await-in-loop
          glied = await kettengliedHex(glied, e);
          if (e.kette !== glied) { ketteHeil = false; bruchBei = i + 1; break; }
        }
        if (cancelled) return;
        setLogbuch({ status: 'ready', eintraege: daten.eintraege, ketteHeil, bruchBei });
      } catch (err) {
        if (!cancelled) setLogbuch({ status: 'error', message: err?.message || 'unbekannter Fehler' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.2, 0.5] },
    );
    NP_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const navTo = (id) => {
    setActiveId(id);
    window.location.hash = id;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (window.matchMedia(MOBILE_TOC_MQ).matches) setTocOpen(false);
  };

  const countAnchors = useCallback(async () => {
    setAnchorBusy(true);
    try {
      const maps = [];
      if (live.bundleUrl) maps.push(`${live.bundleUrl}.map`);
      if (live.workerUrl) maps.push(`${live.workerUrl}.map`);
      const texts = await Promise.all(maps.map(async (m) => {
        const r = await fetch(m, { cache: 'force-cache' });
        return r.ok ? r.text() : '';
      }));
      const joined = texts.join('\n');
      const bundleText = await (await fetch(live.bundleUrl, { cache: 'force-cache' })).text();
      setAnchorCounts(ANCHORS.map((a) => ({
        term: a.term,
        inMaps: countOccurrences(joined, a.term),
        inBundle: countOccurrences(bundleText, a.term),
      })));
    } catch {
      setAnchorCounts('error');
    } finally {
      setAnchorBusy(false);
    }
  }, [live.bundleUrl, live.workerUrl]);

  const ready = live.status === 'ready';
  const forbiddenClean = ready && live.forbidden.every((f) => f.count === 0);

  // Steht der Pruefwert, den dein Browser gerade selbst ausgerechnet hat, im
  // Logbuch? Absichtlich nur ueber den Bundle-Hash, nicht ueber den Dateinamen:
  // Namen kann man frei vergeben, den Inhalt nicht.
  const logTreffer = logbuch.status === 'ready' && ready
    ? logbuch.eintraege.find((e) => e.bundle?.sha256 === live.bundleHash)
    : null;
  const logGeprueft = logbuch.status === 'ready' && ready;
  // Eintraege ohne bestaetigte Archivkopie. Wird nur aus dem Logbuch abgeleitet,
  // nie hart hingeschrieben: die Zahl muss mitwandern, wenn eine Kopie nachtraeglich
  // auftaucht. Eine gedruckte Zahl waere hier nach dem naechsten Build eine Luege.
  const ohneKopie = logbuch.status === 'ready' && logbuch.ketteHeil
    ? logbuch.eintraege.filter((e) => !e.archiv?.stempel)
    : [];

  return (
    <div className="docs-page ed-page np-page min-h-screen">
      <header className="docs-header">
        <div className="docs-header-inner">
          <a href="/" className="docs-brand">
            <HardDrive className="w-6 h-6" />
            <span>
              <span className="docs-brand-title">Tresor</span>
              <span className="ed-eyebrow">Quelltext nachprüfen</span>
            </span>
          </a>
          <nav className="docs-header-nav">
            <a href="/docs" className="ed-link text-sm">Dokumentation</a>
          </nav>
        </div>
      </header>

      <div className="docs-layout">
        <aside className="docs-sidebar">
          <button
            type="button"
            className="docs-toc-toggle"
            aria-expanded={tocOpen}
            aria-controls="np-toc-nav"
            onClick={() => setTocOpen((open) => !open)}
          >
            <span>Inhaltsverzeichnis</span>
            <ChevronDown className="docs-toc-toggle-icon" aria-hidden="true" />
          </button>
          <p className="ed-eyebrow mb-4">Inhalt</p>
          <nav
            id="np-toc-nav"
            aria-label="Seitennavigation"
            className={`docs-toc-nav ${tocOpen ? 'is-open' : ''}`}
          >
            {NP_SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => { e.preventDefault(); navTo(s.id); }}
                className={`docs-nav-item ${activeId === s.id ? 'is-active' : ''}`}
              >
                <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-50" />
                {s.label}
              </a>
            ))}
          </nav>
        </aside>

        <main className="docs-main">
          <section id="was-das-ist" className="docs-section">
            <h2 className="docs-h2"><ShieldCheck className="docs-h2-icon" /> Was diese Seite beweist, und was nicht</h2>
            <p>
              Der Tresor verschlüsselt Ihre Dateien auf Ihrem Gerät. Das ist eine Behauptung.
              Diese Seite zeigt Ihnen, wie Sie sie selbst überprüfen, ohne uns zu glauben und ohne
              Spezialwerkzeug. Sie brauchen nur die Entwicklertools, die in Ihrem Browser schon
              eingebaut sind.
            </p>

            <div className="np-caveat">
              <p className="np-caveat-title"><AlertTriangle className="np-caveat-icon" aria-hidden="true" /> Der wichtigste Satz zuerst</p>
              <p>
                Alles auf dieser Seite beweist, was <strong>Ihr</strong> Browser <strong>heute</strong> in{' '}
                <strong>dieser</strong> Sitzung ausgeführt hat. Es beweist nicht, was unser Server Ihnen morgen
                schickt. Wer eine Web-Anwendung ausliefert, könnte einem einzelnen Nutzer jederzeit anderen
                Code schicken als allen anderen. Dagegen hilft keine Prüfung im Browser, sondern nur ein
                unabhängig veröffentlichter Prüfwert, gegen den viele Leute gleichzeitig vergleichen.
                Wir schreiben das hierhin und nicht ins Kleingedruckte, weil es die ehrliche Grenze
                dieses Verfahrens ist. Was wir dagegen tun können, steht im Abschnitt{' '}
                <a href="#beweis-logbuch" className="ed-link">Das Logbuch</a>.
              </p>
            </div>

            <p>
              Für diese Seite brauchen Sie kein Konto. Sie können alles hier nachprüfen, bevor Sie sich
              anmelden, und das ist Absicht. Wer wissen will, ob er uns seine Dateien anvertraut, soll das
              vorher herausfinden können und nicht erst danach.
            </p>
            <p>
              Im Tresor selbst gibt es dafür noch eine zweite Ansicht, den Maschinenraum. Der läuft nur
              angemeldet und zeigt Ihnen Schritt für Schritt mit, was gerade mit einer konkreten Datei
              passiert. Das ist die Frage nach <em>Ihrer Datei</em>. Hier geht es um die Frage nach dem{' '}
              <em>Code</em>: läuft in Ihrem Browser überhaupt das, was wir beschreiben. Zwei verschiedene
              Fragen, deshalb zwei getrennte Ansichten.
            </p>
          </section>

          <section id="deine-sitzung" className="docs-section">
            <h2 className="docs-h2"><Fingerprint className="docs-h2-icon" /> Ihre Sitzung, gerade eben</h2>
            <p>
              Die folgenden Angaben sind nicht eingetippt. Sie werden ausgelesen, während Sie diese Seite
              ansehen. Die Dateinamen enthalten einen Prüfwert des Inhalts und ändern sich bei jeder
              Änderung am Code.
            </p>

            {live.status === 'loading' && <p className="docs-muted">Wird ausgelesen…</p>}
            {live.status === 'error' && (
              <p className="np-verdict is-bad">Auslesen fehlgeschlagen: {live.message}. Das ist selbst ein Befund. Sagen Sie uns Bescheid.</p>
            )}

            {ready && (
              <div className="docs-table-wrap">
                <table className="ed-table docs-table np-table">
                  <tbody>
                    <tr>
                      <th scope="row">Haupt-Bundle</th>
                      <td>
                        <a className="ed-link np-mono" href={live.bundleUrl} target="_blank" rel="noreferrer">
                          {new URL(live.bundleUrl).pathname} <ExternalLink className="np-inline-icon" aria-hidden="true" />
                        </a>
                        <div className="np-meta">{live.bundleBytes.toLocaleString('de-DE')} Bytes</div>
                        <div className="np-meta np-mono np-hash">SHA-256 {live.bundleHash}</div>
                        <CopyButton value={live.bundleHash} label="Prüfwert kopieren" />
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Krypto-Worker</th>
                      <td>
                        <a className="ed-link np-mono" href={live.workerUrl} target="_blank" rel="noreferrer">
                          {new URL(live.workerUrl).pathname} <ExternalLink className="np-inline-icon" aria-hidden="true" />
                        </a>
                        <div className="np-meta">{live.workerBytes.toLocaleString('de-DE')} Bytes</div>
                        <div className="np-meta np-mono np-hash">SHA-256 {live.workerHash}</div>
                        <CopyButton value={live.workerHash} label="Prüfwert kopieren" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <p className="docs-muted">
              Den Prüfwert rechnet Ihr Browser selbst aus, mit derselben eingebauten Funktion, die auch
              der Tresor benutzt. Wir liefern ihn nicht mit. Vergleichen Sie ihn mit dem, was andere Leute sehen.
              Stimmen die Werte überein, bekommen alle dieselbe Datei.
            </p>
            <div className="np-caveat">
              <p className="np-caveat-title">
                <AlertTriangle className="np-caveat-icon" aria-hidden="true" /> Vorher einmal frisch laden
              </p>
              <p className="docs-muted">
                Ihr Browser speichert diese Dateien lange zwischen. Wenn Sie den Tresor schon länger
                benutzen, rechnet Ihr Browser den Prüfwert womöglich über eine ältere Fassung aus, die
                noch bei Ihnen liegt. Dann weicht Ihr Wert von dem eines neuen Besuchers ab, ohne dass
                irgendetwas faul wäre. Drücken Sie einmal Strg+Shift+R, bevor Sie Prüfwerte mit jemandem
                vergleichen. Das holt alles frisch vom Server.
              </p>
            </div>
          </section>

          <section id="beweis-logbuch" className="docs-section">
            <h2 className="docs-h2"><BookLock className="docs-h2-icon" /> Das Logbuch, und warum eine Kopie davon nicht reicht</h2>
            <p>
              Ganz oben steht der unangenehmste Satz dieser Seite: gegen Code, der gezielt nur an Sie
              ausgeliefert wird, hilft keine Prüfung in Ihrem eigenen Browser. Dieser Abschnitt ist der
              Versuch, wenigstens einen Teil davon einzuholen.
            </p>
            <p>
              Bei jeder Veröffentlichung schreiben wir die Prüfwerte der beiden Dateien in ein Logbuch. Das
              liegt offen unter{' '}
              <a className="ed-link np-mono" href="/pruefwerte.json" target="_blank" rel="noreferrer">
                /pruefwerte.json <ExternalLink className="np-inline-icon" aria-hidden="true" />
              </a>
              . Jeder Eintrag trägt zusätzlich ein Kettenglied: einen Prüfwert über den Vorgänger und sich
              selbst. Wer einen alten Eintrag nachträglich umschreibt, zerreißt damit alle folgenden. Ihr
              Browser rechnet diese Kette gerade nach.
            </p>

            <div className="np-caveat">
              <p className="np-caveat-title">
                <AlertTriangle className="np-caveat-icon" aria-hidden="true" /> Warum hier kein grüner Haken steht
              </p>
              <p>
                Diese Datei liegt auf demselben Server wie der Code, den sie bestätigen soll. Wer Ihnen gezielt
                ein anderes Bundle schickt, kann Ihnen genauso gut ein dazu passendes Logbuch schicken, samt
                heiler Kette. Deshalb gilt hier eine Asymmetrie, und wir halten uns daran: <strong>Wenn Ihr
                Prüfwert fehlt, ist das ein echtes Warnzeichen.</strong> Wenn er drinsteht, ist das nur eine
                Tatsache und kein Freispruch. Der Freispruch kommt erst aus der Archivkopie weiter unten.
              </p>
            </div>

            {logbuch.status === 'loading' && <p className="docs-muted">Logbuch wird geprüft…</p>}
            {logbuch.status === 'error' && (
              <p className="np-verdict is-bad np-block">
                Logbuch nicht abrufbar: {logbuch.message}. Das ist selbst ein Befund. Sagen Sie uns Bescheid.
              </p>
            )}
            {logbuch.status === 'ready' && !logbuch.ketteHeil && (
              <p className="np-verdict is-bad np-block">
                Die Kette bricht bei Eintrag {logbuch.bruchBei}. Das Logbuch wurde nachträglich verändert.
                Verlassen Sie sich ab hier auf keinen einzigen Wert daraus und schreiben Sie uns.
              </p>
            )}

            {logbuch.status === 'ready' && logbuch.ketteHeil && (
              <div className="docs-table-wrap">
                <table className="ed-table docs-table np-table">
                  <thead>
                    <tr>
                      <th scope="col">Stand (UTC)</th>
                      <th scope="col">Haupt-Bundle</th>
                      <th scope="col">Prüfwert (Anfang)</th>
                      <th scope="col">Archiv</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logbuch.eintraege.map((e) => (
                      <tr key={e.kette} className={logTreffer && logTreffer.kette === e.kette ? 'np-log-hit' : undefined}>
                        <td className="np-mono">{e.stand.slice(0, 10)} {e.stand.slice(11, 16)}</td>
                        <td className="np-mono">{e.bundle.datei.replace('/assets/', '')}</td>
                        <td className="np-mono np-hash">{e.bundle.sha256.slice(0, 16)}…</td>
                        <td>
                          {/* Link nur, wenn im Logbuch ein gepruefter Zeitstempel steht. Ein Link
                              ins Leere waere schlimmer als gar keiner: er sieht aus wie ein Beweis.
                              Die Adresse selbst bleibt berechnet (web/2/ = neuester Schnappschuss
                              dieser Adresse), nicht aus dem gespeicherten Stempel gebaut. Den
                              koennten wir setzen, wohin wir wollen. */}
                          {e.archiv?.stempel ? (
                            <a className="ed-link np-mono" href={archivUrl(e.stand, e.archiv.stempel)} target="_blank" rel="noreferrer">
                              ansehen <ExternalLink className="np-inline-icon" aria-hidden="true" />
                            </a>
                          ) : (
                            <span className="docs-muted np-mono">keine Kopie</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {logGeprueft && logbuch.ketteHeil && logTreffer && (
              <p className="np-fact">
                Der Prüfwert, den Ihr Browser oben selbst ausgerechnet hat, steht im Logbuch. Eintrag vom{' '}
                <span className="np-mono">{logTreffer.stand.slice(0, 10)}, {logTreffer.stand.slice(11, 16)} UTC</span>. Verglichen wurde der
                Prüfwert des Inhalts, nicht der Dateiname. Einen Namen kann man frei vergeben, einen Inhalt nicht.
              </p>
            )}
            {logGeprueft && logbuch.ketteHeil && !logTreffer && (
              <p className="np-verdict is-bad np-block">
                Ihr Prüfwert steht nicht im Logbuch. Häufigster harmloser Grund: Ihr Browser hat noch eine
                ältere Fassung liegen. Drücken Sie Strg+Shift+R und sehen Sie noch einmal nach. Bleibt es dabei,
                schreiben Sie uns.
              </p>
            )}

            <h3 className="docs-h3">Der Schritt, der wirklich zählt</h3>
            <p>
              Vergleichen Sie das Logbuch mit einer Kopie, die wir nicht bearbeiten können. Zu jedem Eintrag, der
              es ins Internet Archive geschafft hat, liegt dort eine eigene Fassung mit Zeitstempel. In der
              Tabelle oben führt die Spalte <strong>Archiv</strong> direkt dorthin. Wenn wir einen alten
              Eintrag stillschweigend ändern würden, stünde die alte Fassung weiterhin dort. Wo in der Spalte
              <strong> keine Kopie</strong> steht, gibt es diesen Vergleich nicht. Warum, steht weiter unten.
            </p>
            <p>
              Was Sie dort sehen, ist der komplette Stand des Logbuchs zum Zeitpunkt der Kopie. Der Prüfwert,
              den Ihr Browser oben ausgerechnet hat, muss darin auftauchen. Tut er das, hat jemand
              ausserhalb unserer Reichweite festgehalten, dass dieser Wert <strong>spätestens</strong> zum
              Zeitstempel der Kopie in unserem Logbuch stand. Mehr sagt ein Schnappschuss nicht aus, und
              weniger auch nicht.
            </p>
            <p className="docs-muted">
              Der Zeitstempel in diesen Links steht in unserem Logbuch, also könnten wir ihn theoretisch
              auf einen anderen Schnappschuss zeigen lassen. Wenn Sie das ausschliessen wollen, nehmen Sie den
              Zeitstempel aus der Adresse heraus und ersetzen Sie ihn durch eine <span className="np-mono">2</span>.
              Dann sucht das Archiv selbst den neuesten Stand zu dieser Adresse. Direkt nach einer
              Veröffentlichung landen Sie damit kurzzeitig auf der Speicherseite des Archivs, weil dessen
              Index ein paar Minuten hinterherhinkt. Der festgenagelte Link funktioniert dann schon.
            </p>
            <p className="docs-muted">
              Jeder Eintrag bekommt eine eigene Adresse, weil das Archiv sonst dedupliziert und bei zwei
              Veröffentlichungen kurz hintereinander nur die erste ablegt. Die Adressen unterscheiden sich
              nur im angehängten Zeitstempel und liefern alle dieselbe Datei.{' '}
              <a
                className="ed-link np-mono"
                href="https://web.archive.org/web/2/https://tresor.datenputzer.de/pruefwerte.json"
                target="_blank"
                rel="noreferrer"
              >
                Neueste archivierte Fassung <ExternalLink className="np-inline-icon" aria-hidden="true" />
              </a>
            </p>
            <p className="docs-muted">
              Diesen Vergleich müssen Sie selbst anklicken, wir holen ihn absichtlich nicht automatisch. Würde
              diese Seite das Archiv im Hintergrund abfragen, müsste in unserer Sicherheitsregel eine vierte
              Adresse stehen. Dann wäre der Satz in Abschnitt 2 nicht mehr wahr, dass Ihr Browser mit genau
              drei Adressen spricht. Ein Beweis, der einen anderen Beweis kaputt macht, ist keiner.
            </p>

            {ohneKopie.length > 0 && (
              <div className="np-caveat">
                <p className="np-caveat-title">
                  <AlertTriangle className="np-caveat-icon" aria-hidden="true" />{' '}
                  {ohneKopie.length} von {logbuch.eintraege.length} Einträgen haben keine Archivkopie
                </p>
                <p>
                  In der Spalte <strong>Archiv</strong> steht dort <span className="np-mono">keine Kopie</span>.
                  Für diese Einträge gibt es nichts, was unabhängig von uns bestätigt, dass sie so
                  veröffentlicht wurden. Sie sind eine Behauptung, kein Beweis.
                </p>
                <p>
                  Beim jüngsten Eintrag ist das meistens nur Verzögerung. Das Archiv zieht nicht sofort nach,
                  und wir tragen den Zeitstempel nach, sobald die Kopie auftaucht. Bei älteren Einträgen ist
                  es eine echte Lücke. Eigene Adressen je Eintrag gibt es erst seit dem 27.08.2026. Davor lief
                  alles über eine einzige Adresse, und das Archiv hat die Wiederholungen als Dubletten verworfen.
                </p>
                <p>
                  Nachholen werden wir das nicht, und zwar mit Absicht. Eine heute angelegte Kopie eines alten
                  Eintrags würde nicht zeigen, was damals ausgeliefert wurde, sondern was wir heute ausliefern.
                  Genau diesen Griff hätte auch jemand frei, der einen alten Eintrag umschreibt. Dann sähe die
                  Spalte vollständig aus und wäre wertlos. Eine Lücke gehört benannt, nicht gefüllt.
                </p>
              </div>
            )}
          </section>

          <section id="beweis-worker" className="docs-section">
            <h2 className="docs-h2"><FileCode className="docs-h2-icon" /> 1. Der Worker, in voller Länge</h2>
            <p>
              Das ist der schnellste Beweis, und deshalb steht er zuerst. Die eigentliche
              Verschlüsselung läuft in einem eigenen kleinen Programm, einem Worker. Diese Datei ist so
              klein, dass Sie sie ganz lesen können, auch ohne Programmierkenntnisse. Sie steht unten
              vollständig da, geholt von derselben Adresse, von der Ihr Browser sie holt.
            </p>
            <p>
              Zwei Dinge sind daran wichtig. Erstens: in dieser Datei steht keine einzige Anweisung, die
              etwas ins Netz schicken könnte. Zweitens: in dieser Datei steht nichts über Passwörter. Der
              Worker bekommt einen fertigen Schlüssel gereicht und weiß nicht, woher der kommt.
            </p>

            {ready && (
              <>
                <div className="docs-table-wrap">
                  <table className="ed-table docs-table np-table">
                    <thead>
                      <tr><th>Gesucht</th><th>Bedeutung</th><th>Treffer</th></tr>
                    </thead>
                    <tbody>
                      {live.forbidden.map((f) => (
                        <tr key={f.term}>
                          <td className="np-mono">{f.term}</td>
                          <td className="np-meta">{f.why}</td>
                          <td><Verdict ok={f.count === 0}>{f.count}</Verdict></td>
                        </tr>
                      ))}
                      {live.expected.map((f) => (
                        <tr key={f.term}>
                          <td className="np-mono">{f.term}</td>
                          <td className="np-meta">{f.why}</td>
                          <td><Verdict ok={f.count > 0}>{f.count}</Verdict></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className={forbiddenClean ? 'np-verdict is-ok np-block' : 'np-verdict is-bad np-block'}>
                  {forbiddenClean
                    ? 'Kein einziger der Begriffe, die dort nicht hingehören, kommt vor.'
                    : 'Achtung: mindestens ein Begriff kommt vor, der dort nicht hingehört. Bitte melden Sie das.'}
                </p>

                <p className="docs-muted">
                  Diese Zählung haben wir gemacht, also müssen Sie ihr nicht trauen. Machen Sie die Datei über den
                  Link oben selbst auf und drücken Sie Strg+F. Es dauert zwanzig Sekunden. Der ganze Sinn dieser
                  Tabelle ist, Ihnen zu zeigen, wonach Sie suchen sollen.
                </p>

                <figure className="docs-code-block">
                  <figcaption className="docs-code-caption">
                    Vollständiger Inhalt von {new URL(live.workerUrl).pathname}
                  </figcaption>
                  <pre className="docs-code np-worker-source"><code>{live.workerText}</code></pre>
                </figure>

                <p className="docs-muted">
                  Der Code ist verkürzt, weil er für die Auslieferung zusammengestaucht wird. Variablen
                  heißen deshalb <span className="np-mono">a</span> oder <span className="np-mono">n</span>{' '}
                  statt ausgeschrieben. Die Struktur bleibt lesbar: zwei Funktionen, eine verschlüsselt
                  einen Block, eine entschlüsselt ihn, und eine Stelle, die Nachrichten von der Anwendung
                  entgegennimmt. Mehr ist da nicht.
                </p>
              </>
            )}
          </section>

          <section id="beweis-netzwerk" className="docs-section">
            <h2 className="docs-h2"><Network className="docs-h2-icon" /> 2. Wohin Ihr Browser überhaupt spricht</h2>
            <ol className="docs-ol">
              <li>F12 drücken, Reiter <strong>Netzwerk</strong> öffnen.</li>
              <li>Seite mit Strg+R neu laden, damit alles mitgeschnitten wird.</li>
              <li>Die Spalte <strong>Domain</strong> einblenden. Rechtsklick auf die Spaltenüberschrift, dann Domain anhaken.</li>
              <li>Nach Domain sortieren und die Liste ansehen.</li>
            </ol>
            <p>
              Sie werden genau drei Adressen finden: <span className="np-mono">tresor.datenputzer.de</span>,{' '}
              <span className="np-mono">datenputzer.de</span> für die Anmeldung und{' '}
              <span className="np-mono">nbg1.your-objectstorage.com</span> für den Speicher selbst. Eine
              vierte gibt es nicht. Kein Google, kein Analysedienst, kein Werbepixel, kein Schriftarten-Anbieter.
            </p>
            <p className="docs-muted">
              Auf dieser Seite hier sehen Sie zusätzlich eine Anfrage nach{' '}
              <span className="np-mono">/pruefwerte.json</span> und je eine nach den beiden Dateien aus
              dem Abschnitt darüber. Die holt diese Seite selbst, um Ihnen die Prüfwerte auszurechnen. Sie
              gehen an unsere eigene Adresse und kommen auf den anderen Seiten des Tresors nicht vor.
            </p>
            <p>
              Das müssen Sie nicht als Versprechen nehmen. Unser Server schickt bei jedem Aufruf eine
              Sicherheitsregel mit, die dem Browser verbietet, mit anderen Adressen zu sprechen. Sie heißt
              Content-Security-Policy. Sie sehen sie im Netzwerk-Reiter, wenn Sie die erste Anfrage
              anklicken und unter <strong>Antwortheader</strong> nachsehen. Wenn wir es uns morgen anders
              überlegen und einen Fremdanbieter einbauen, müssten wir diese Regel öffnen, und das ist von
              außen sichtbar.
            </p>
            <p className="docs-muted">
              Der Speicher-Anbieter sieht dabei verschlüsselte Blöcke und wie groß sie sind. Er sieht keine
              Dateinamen und keine Inhalte. Was er trotzdem erfährt, steht offen in der{' '}
              <a href="/docs#metadaten" className="ed-link">Dokumentation unter Metadaten</a>.
            </p>
          </section>

          <section id="beweis-baum" className="docs-section">
            <h2 className="docs-h2"><Search className="docs-h2-icon" /> 3. Läuft wirklich diese Datei?</h2>
            <p>
              Eine Datei zu lesen, die irgendwo herumliegt, beweist noch nichts. Die Frage ist, ob genau
              diese Datei ausgeführt wird. Dafür gibt es zwei kurze Wege.
            </p>
            <h3 className="docs-h3">Weg A: die Konsole fragen</h3>
            <p>
              Entwicklertools öffnen, Reiter <strong>Konsole</strong>, folgende Zeile einfügen und Enter drücken.
              Sie listet auf, welche Skripte diese Seite tatsächlich geladen hat.
            </p>
            <figure className="docs-code-block">
              <figcaption className="docs-code-caption">In die Konsole einfügen</figcaption>
              <pre className="docs-code"><code>[...document.scripts].map(s =&gt; s.src).filter(Boolean)</code></pre>
            </figure>
            <CopyButton value="[...document.scripts].map(s => s.src).filter(Boolean)" label="Befehl kopieren" />
            <p className="docs-muted">
              Manche Browser verlangen beim ersten Mal, dass Sie <span className="np-mono">erlauben</span> eintippen,
              bevor Einfügen in die Konsole freigeschaltet wird. Das ist ein Schutz vor Betrugsmaschen und
              völlig in Ordnung.
            </p>

            <h3 className="docs-h3">Weg B: den Worker im Baum sehen</h3>
            <p>
              Reiter <strong>Quellen</strong> öffnen, dann links in der Leiste den Bereich{' '}
              <strong>Threads</strong> aufklappen. Solange der Tresor entsperrt ist und Sie eine Datei
              hoch- oder herunterladen, taucht dort ein zweiter Eintrag neben dem Hauptfenster auf. Das ist
              der Krypto-Worker. Klicken Sie ihn an, dann sehen Sie genau die Datei, die er ausführt, und können
              den Namen mit dem aus Abschnitt 1 vergleichen.
            </p>
          </section>

          <section id="beweis-haltepunkt" className="docs-section">
            <h2 className="docs-h2"><Bug className="docs-h2-icon" /> 4. Haltepunkt setzen und in den Klartext sehen</h2>
            <p>
              Das ist der Beweis, der wirklich zählt, denn er zeigt Ihnen die Daten in dem Moment, in dem sie
              verschlüsselt werden. Wir liefern seit dem 27.08.2026 absichtlich Sourcemaps mit. Das sind
              Übersetzungshilfen, mit denen Ihr Browser den zusammengestauchten Code wieder in den
              ursprünglichen, lesbaren Quelltext zurückverwandelt, samt unserer Kommentare. Viele Anbieter
              lassen die weg. Im Frontend liegt kein Geheimnis, also gibt es keinen Grund dafür.
            </p>
            <ol className="docs-ol">
              <li>Entwicklertools öffnen, Reiter <strong>Quellen</strong>.</li>
              <li>Strg+Shift+F drücken. Das ist die Suche über alle geladenen Dateien.</li>
              <li>Einen der Begriffe aus der Tabelle unten eingeben.</li>
              <li>Auf den Treffer klicken. Sie landen im lesbaren Original, nicht im gestauchten Code.</li>
              <li>Links auf die Zeilennummer klicken, um dort anzuhalten. Dann im Tresor eine Datei hochladen.</li>
              <li>Der Browser hält an. Fahren Sie mit der Maus über die Variablen, dann sehen Sie die echten Werte.</li>
            </ol>

            <div className="docs-table-wrap">
              <table className="ed-table docs-table np-table">
                <thead>
                  <tr><th>Suchbegriff</th><th>Wo das hinführt</th><th>Treffer</th></tr>
                </thead>
                <tbody>
                  {ANCHORS.map((a, i) => {
                    const c = Array.isArray(anchorCounts) ? anchorCounts[i] : null;
                    return (
                      <tr key={a.term}>
                        <td>
                          <span className="np-mono np-anchor-term">{a.term}</span>
                          <CopyButton value={a.term} label="Suchbegriff kopieren" />
                        </td>
                        <td>
                          <div>{a.where}</div>
                          <div className="np-meta">{a.note}</div>
                        </td>
                        <td className="np-mono">
                          {c
                            ? <>{c.inMaps} im Quelltext<br />{c.inBundle} im gestauchten Code</>
                            : <span className="np-meta">noch nicht gezählt</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button type="button" className="np-btn" onClick={countAnchors} disabled={!ready || anchorBusy}>
              {anchorBusy ? 'Wird gezählt…' : 'Treffer jetzt live nachzählen'}
            </button>
            {anchorCounts === 'error' && (
              <p className="np-verdict is-bad np-block">Nachzählen fehlgeschlagen. Das ist selbst ein Befund.</p>
            )}
            <p className="docs-muted">
              Suchen Sie am besten aus der Anwendung heraus und nicht von dieser Seite aus. Die Suchbegriffe
              stehen hier ja als Text auf dem Bildschirm, und die Suche der Entwicklertools durchsucht
              auch die Seite, die Sie gerade ansehen. Im ausgelieferten Programmcode haben wir sie
              absichtlich zerlegt hinterlegt, damit sie Ihre Suche nicht verfälschen.
            </p>
            <p className="docs-muted">
              Wir schreiben die Trefferzahlen nicht fest hin, sondern zählen sie auf Knopfdruck in dem
              Code, den Sie gerade geladen haben. Eine festgetippte Zahl wäre spätestens nach dem nächsten
              Update falsch. Das Nachzählen lädt die Übersetzungshilfen herunter, das sind ein paar
              Megabyte, deshalb passiert es erst, wenn Sie es wollen.
            </p>

            <div className="np-caveat">
              <p className="np-caveat-title"><AlertTriangle className="np-caveat-icon" aria-hidden="true" /> Wenn Sie nur gestauchten Code sehen</p>
              <p>
                Dann liegt vermutlich eine ältere Fassung in Ihrem Browser-Zwischenspeicher, aus der Zeit
                vor den Sourcemaps. Drücken Sie einmal Strg+Shift+R. Das lädt alles frisch, und danach ist der
                lesbare Quelltext da.
              </p>
            </div>
          </section>

          <section id="beweis-coverage" className="docs-section">
            <h2 className="docs-h2"><Gauge className="docs-h2-icon" /> 5. Nachsehen, welche Zeilen wirklich liefen</h2>
            <p>
              Es reicht nicht, dass guter Code da ist. Er muss auch ausgeführt werden. Dafür haben die
              Entwicklertools ein Werkzeug namens Coverage, auf Deutsch Abdeckung.
            </p>
            <ol className="docs-ol">
              <li>Entwicklertools öffnen, Strg+Shift+P drücken, <span className="np-mono">Coverage</span> eintippen und den Eintrag zum Anzeigen wählen.</li>
              <li>Auf Aufzeichnen drücken, dann im Tresor eine Datei hochladen.</li>
              <li>Auf das Bundle doppelklicken. Ausgeführte Zeilen sind grün markiert, nie ausgeführte rot.</li>
            </ol>
            <p>
              So sehen Sie zum Beispiel, dass die Verschlüsselungsfunktion beim Hochladen tatsächlich
              durchlaufen wird und nicht nur dekorativ herumsteht.
            </p>
          </section>

          <section id="beweis-override" className="docs-section">
            <h2 className="docs-h2"><Replace className="docs-h2-icon" /> 6. Der Gegenbeweis: Code ändern und zusehen</h2>
            <p>
              Das ist der strengste Test auf dieser Seite. Alle bisherigen Schritte zeigen Ihnen Code. Dieser
              hier zeigt Ihnen, dass es <em>derselbe</em> Code ist, der läuft. Denn wenn Sie ihn kaputt machen
              und die Anwendung daraufhin kaputt geht, dann war es der echte.
            </p>
            <ol className="docs-ol">
              <li>Reiter <strong>Quellen</strong>, links den Bereich <strong>Überschreibungen</strong> öffnen und einen Ordner auf Ihrer Festplatte dafür auswählen.</li>
              <li>Den Krypto-Worker aus Abschnitt 1 im Baum suchen und im Kontextmenü das Überschreiben einschalten.</li>
              <li>Irgendetwas darin verändern. Zum Beispiel <span className="np-mono">"AES-GCM"</span> in <span className="np-mono">"AES-XXX"</span> ändern, dann speichern.</li>
              <li>Seite neu laden und eine Datei hochladen.</li>
            </ol>
            <p>
              Der Upload muss jetzt scheitern. Tut er das, haben Sie bewiesen: die Datei, die Sie gelesen haben,
              ist die Datei, die Ihre Dateien verschlüsselt. Nicht eine Kopie, nicht eine
              Beruhigungsfassung für neugierige Nutzer.
            </p>
            <p className="docs-muted">
              Danach die Überschreibung wieder abschalten, sonst bleibt sie in Ihrem Browser aktiv und der
              Tresor funktioniert weiter nicht. Diese Änderung passiert nur bei Ihnen und erreicht unseren
              Server nie.
            </p>
          </section>

          <section id="grenzen" className="docs-section">
            <h2 className="docs-h2"><AlertTriangle className="docs-h2-icon" /> Grenzen, ohne Schönfärberei</h2>
            <ul className="docs-list">
              <li>
                <strong>Es gilt für diese Sitzung.</strong> Alles hier prüft den Code, den Sie gerade geladen
                haben. Ein Anbieter könnte einem einzelnen Nutzer gezielt anderen Code schicken. Dagegen hilft
                nur, Prüfwerte öffentlich zu vergleichen. Deshalb steht der Prüfwert oben zum Kopieren.
              </li>
              <li>
                <strong>Das Logbuch steht auf unserem eigenen Server.</strong> Es gibt eine{' '}
                <a href="#beweis-logbuch" className="ed-link">fortlaufende Liste der Prüfwerte</a>, und ihre
                Einträge sind untereinander verkettet. Gegen jemanden, der Ihnen gezielt ein anderes Bundle
                schickt, hilft trotzdem erst der Vergleich mit der Archivkopie, weil nur die außerhalb
                unserer Reichweite liegt.
              </li>
              <li>
                <strong>Ihr Browser und Ihr Betriebssystem bleiben außen vor.</strong> Wer Ihren Rechner
                kontrolliert, sieht Ihre Dateien, bevor sie überhaupt verschlüsselt werden. Keine
                Verschlüsselung der Welt ändert daran etwas.
              </li>
              <li>
                <strong>Erweiterungen können mitlesen.</strong> Eine Browser-Erweiterung mit Vollzugriff auf
                die Seite kann alles sehen, was Sie sehen. Für einen ernsthaften Test nehmen Sie ein frisches
                Profil ohne Erweiterungen.
              </li>
              <li>
                <strong>Das hier prüft den Client.</strong> Was auf dem Server läuft, können Sie von außen
                nicht nachsehen. Der ganze Aufbau ist genau deshalb so gebaut, dass es egal ist: der Server
                bekommt nie Ihren Schlüssel. Ob das stimmt, prüfen Sie an dem, was Ihr Browser rausschickt,
                und das steht in Abschnitt 2 und 4.
              </li>
            </ul>
            <p>
              Finden Sie etwas, das nicht zu dieser Beschreibung passt, schreiben Sie uns:{' '}
              <a href="mailto:support@datenputzer.de" className="ed-link">support@datenputzer.de</a>.
              Ein Widerspruch zwischen dieser Seite und dem echten Code ist ein Fehler, den wir wissen wollen.
            </p>
          </section>

          <footer className="docs-footer">
            <hr className="ed-rule" />
            <p className="ed-eyebrow">
              Datenputzer Tresor · Quelltext nachprüfen · Alle Zahlen auf dieser Seite werden in Ihrem Browser gemessen
            </p>
          </footer>
        </main>
      </div>

      <SiteFooter />
    </div>
  );
}
